//
//  trials.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the trial, trial QA and trial participant pages.
//

import { Permission } from "src/api/Tmd.dtos";

import { dashboardSummaryTourTarget, helpTourTargets } from "../helpTourTargets";
import {
    emailRecipientsGuide,
    entryGuide,
    notesGuides,
    participantColumns,
    participantGuide,
    versionStops
} from "./commonGuides";
import {
    actionsStop,
    column,
    covering,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    menu,
    saveStop,
    stop,
    type PageGuide
} from "./pageGuide";
import {
    roleCreateGuide,
    roleEditGuide,
    roleListGuide,
    type RoleOwner
} from "./personnelRoleGuides";

const trialDetails: PageGuide = {
    intro: "The trial's identity, classification, status and the groups responsible for it, with its registrations, chairs and funding. The side menu opens everything else about the trial.",
    stops: [
        field(
            "studyAcronym",
            "Acronym",
            "The short name the trial is known by, such as PROSPECT. It is shown in lists, breadcrumbs and emails."
        ),
        field(
            "title",
            "Scientific Title",
            "The full scientific title from the protocol. It must be unique across trials."
        ),
        field(
            "laySummary",
            "Lay Summary",
            "A plain-language description of the trial for public and member communications."
        ),
        field(
            "newProposalNumber",
            "New Proposal Number",
            "The number given when the trial was first proposed, in the format NP 000."
        ),
        field(
            "trialDevelopmentNumber",
            "Trial Development Number",
            "The number given while the trial is in development, in the format TD 00.00."
        ),
        field(
            "allIdentifiers",
            "Trial Identifiers",
            "Every number the trial is known by, combined for display in lists and search. It is worked out from the numbers above."
        ),
        field(
            "protocolId",
            "TROG Number",
            "The trial's TROG number, such as TROG 18.06. It must be unique, and when the category checks a format the number must match it."
        ),
        field(
            "trialCategoryId",
            "Category",
            "Groups the trial for reporting, from the categories maintained in settings. Some categories check the format of the TROG Number."
        ),
        field(
            "trialTrialPhases",
            "Phase",
            "The clinical phase or phases of the trial, such as II or III. More than one can be chosen."
        ),
        field(
            "trialInvolvements",
            "TROG Involvement",
            "How TROG takes part in the trial, such as leading it or collaborating on an international study."
        ),
        covering(
            field(
                "trialTumourGroups",
                "Tumour Groups",
                "The cancers the trial studies. Choose Other and describe it in Other Tumour Group when one is not listed."
            ),
            ["tumourGroupOther", "Other Tumour Group, enabled when Other is chosen"]
        ),
        field(
            "trialAllocatedWPs",
            "Allocated Working Parties",
            "The TROG working parties the trial is allocated to, which oversee it scientifically."
        ),
        field(
            "trialStatusId",
            "Current Status (TROG)",
            "Where the trial is in its life cycle for TROG, from development through recruiting to closed. Some permissions only apply at certain trial statuses."
        ),
        field(
            "internationalTrialStatusId",
            "Current Status (International)",
            "The status of the international study, for trials run with overseas groups."
        ),
        field(
            "australianLeadGroupId",
            "ANZ Group (Sponsor)",
            "The Australian and New Zealand group sponsoring the trial."
        ),
        field(
            "internationalLeadGroupId",
            "International Lead Group (Sponsor)",
            "The overseas group leading the study, for international trials."
        ),
        field(
            "trialCollaboratingGroups",
            "Collaborating Groups",
            "Other cooperative groups taking part in the trial alongside the sponsors."
        ),
        field(
            "centralCoordinatingCentreGroupId",
            "Central Trial Coordinating Centre",
            "The group that coordinates the trial centrally, such as managing data and sites."
        ),
        field(
            "internationalCentralCoordinatingCentreGroupId",
            "International Central Trial Coordinating Centre",
            "The overseas coordinating centre, for international trials."
        ),
        field(
            "centralRTQACoordinatingCentreGroupId",
            "Central RTQA Coordinating Centre",
            "The group that runs radiotherapy quality assurance for the trial, such as benchmarking and case reviews."
        ),
        covering(
            field(
                "trialProjectRegistrations",
                "Project Registrations",
                "The trial's registrations with public trial registries. Add a row per registry with its registration number and link; choose Other and name the registry when it is not listed."
            ),
            ["trialRegistryId", "Registry"],
            ["trialRegistryOther", "Other, enabled when the registry is Other"],
            ["registrationNumber", "Registration Number"],
            ["url", "Url"]
        ),
        covering(
            field(
                "trialChairs",
                "Trial Chairs",
                "The trial's chairs. Add a row per chair and tick Is Primary Contact for the chair to contact first."
            ),
            ["personnelId", "Trial Chair"],
            ["isPrimary", "Is Primary Contact"]
        ),
        covering(
            field(
                "trialFundings",
                "Funding",
                "The trial's funding applications and grants. Add a row per source with its status, the amounts requested and received, and the funding period."
            ),
            ["name", "Name"],
            ["source", "Source"],
            ["fundingStatusId", "Status"],
            ["amountRequested", "Amount Requested"],
            ["submissionDate", "Submission Date"],
            ["amountRecieved", "Amount Recieved"],
            ["currency", "Currency"],
            ["startDate", "Start Date"],
            ["endDate", "End Date"]
        ),
        field(
            "active",
            "Active",
            "Clear to hide the trial from lists and selections. Its records are kept."
        ),
        saveStop(
            "Submit saves the trial. Problems are listed at the top of the form and beside each field.",
            Permission.TrialUpdate
        )
    ],
    rules: [
        "At least one of TROG Number, Trial Development Number or New Proposal Number is required.",
        "New Proposal Number must look like NP 000 and Trial Development Number like TD 00.00.",
        "TROG Number and Scientific Title must not be used by another trial."
    ]
};

const trialDetailsWithMenu: PageGuide = {
    ...trialDetails,
    stops: [
        ...trialDetails.stops,
        menu(
            item("Details", "This page: the trial's identity, classification, status and groups."),
            item(
                "Milestones",
                "The trial's key dates, accrual and site targets, analyses, presentations and publications."
            ),
            item(
                "Institutions",
                "The sites taking part, with their status, accrual and capability coverage."
            ),
            item(
                "Personnel",
                "The people with roles on the trial and at its sites, which decide their permissions and emails."
            ),
            item("Participants", "Every participant registered on the trial, across all sites."),
            item(
                "Items",
                "What the trial collects from itself, its sites, groups and participants, such as agreements and approvals."
            ),
            item(
                "Forms",
                "The trial's QA forms, such as benchmarking exercises and case reviews, with their case reports."
            ),
            item(
                "Groups",
                "The cooperative groups taking part in the trial, and the items collected from each."
            ),
            item(
                "Treatments",
                "The trial's randomisation arms, which participants are allocated to."
            ),
            item("Notes", "Notes recorded about the trial, such as correspondence and decisions."),
            item(
                "QA Tracker",
                "Quality-assurance activities on the trial, such as plan reviews, as they move through milestones."
            ),
            item(
                "QA Program Checklist",
                "The trial's QA program tasks and their completion. Shown once the trial's checklist has been set up."
            ),
            item(
                "Configuration",
                "How the trial works: its item templates, capabilities, tags, emails and QA checklist."
            )
        )
    ]
};

const trialMilestones: PageGuide = {
    intro: "The trial's key dates, accrual and site targets for TROG (Australia and New Zealand) and for the international study, with its analyses, presentations and publications. These feed trial reporting.",
    stops: [
        field(
            "dateTSCApprovedForDevelopment",
            "Date TSC approved for development",
            "When the Trial Scientific Committee approved the proposal for development."
        ),
        field(
            "dateTSCTROGNumberAllocated",
            "Date TROG # allocated",
            "When the trial was given its TROG number, usually on approval to proceed."
        ),
        field(
            "dateTSCFinalProtocol",
            "Date of final Protocol/GSA",
            "When the final protocol or group sponsorship agreement was approved."
        ),
        field(
            "dateTSCHRECApprovalReveived",
            "Date HREC Approval Received",
            "When the lead human research ethics committee approved the trial."
        ),
        covering(
            field(
                "targetAccrual",
                "Target Accrual",
                "How many participants TROG aims to recruit; Total Accrual records how many have been recruited so far."
            ),
            ["totalAccrual", "Total Accrual"]
        ),
        covering(
            field(
                "targetSitesTotal",
                "Target Number of Sites",
                "How many sites are planned and how many have opened, in total and split into Australia and New Zealand."
            ),
            ["actualSitesTotal", "Total Number of Sites"],
            ["targetSitesAUS", "Target Number of Sites AUS"],
            ["actualSitesAUS", "Total Number of Sites AUS"],
            ["targetSitesNZ", "Target Number of Sites NZ"],
            ["actualSitesNZ", "Total Number of Sites NZ"]
        ),
        field(
            "dateActivated",
            "Date TROG activated study",
            "When the first TROG site opened to recruitment."
        ),
        field(
            "dateFirstPatient",
            "Date of TROG first participant",
            "When the first TROG participant was recruited."
        ),
        covering(
            field(
                "targetDateAccrualCompleted",
                "Expected accrual completion date",
                "When recruitment is expected to finish; Date TROG accrual complete records when it actually did."
            ),
            ["dateAccrualComplete", "Date TROG accrual complete"]
        ),
        covering(
            field(
                "targetDateFollowupCompletion",
                "Expected date of follow up completion",
                "When participant follow-up is expected to finish; Date TROG completed follow up records when it actually did."
            ),
            ["dateStudyClosed", "Date TROG completed follow up"]
        ),
        covering(
            field(
                "dateExpectedCompletion",
                "Expected date of completion",
                "When the trial is expected to finish; Date TROG completed/withdrawn records when it finished or was withdrawn."
            ),
            ["dateCompletedWithdrawn", "Date TROG completed/withdrawn"]
        ),
        covering(
            field(
                "internationalTargetAccrual",
                "Expected Accrual",
                "The international study's recruitment target. The fields that follow record its accrual, sites and dates in the same way as TROG's."
            ),
            ["internationalTotalAccrual", "Total Accrual"],
            ["internationalTargetSites", "Expected Number of Sites Other"],
            ["internationalActualSites", "Actual Number of Sites Other"],
            ["internationalDateActivated", "Date activated"],
            ["internationalDateCompletedWithdrawn", "Date completed/withdrawn"],
            ["internationalTargetDateAccrualCompleted", "Expected accrual completion date"],
            ["internationalActualDateAccrualCompleted", "Date accrual completed"],
            ["internationalTargetDateFollowupCompletion", "Expected follow up completion date"],
            ["internationalActualDateFollowupCompletion", "Date follow up completed"]
        ),
        field(
            "comments",
            "Comments",
            "Notes on the milestones, such as the reasons for a delay or a revised target."
        ),
        covering(
            field(
                "trialAnalysisResults",
                "Analysis & Results",
                "The trial's planned and completed analyses. Add a row per analysis with its type, target and actual dates, and the publication reporting it."
            ),
            ["trialAnalysisTypeId", "Analysis Type"],
            ["trialAnalysisTypeOther", "Other, for an analysis type not listed"],
            ["targetStartDate", "Target Start Date"],
            ["actualStartDate", "Actual Start Date"],
            ["targetCompletionDate", "Target Completion Date"],
            ["actualCompletionDate", "Actual Completion Date"],
            ["trialPublicationId", "Publication"]
        ),
        covering(
            field(
                "trialPresentations",
                "Presentations",
                "Presentations of the trial at meetings. Add a row per presentation with its type, title, presenter, meeting and date, and whether it was published as an abstract."
            ),
            ["trialPresentationTypeId", "Presentation Type"],
            ["title", "Title"],
            ["presenterName", "Name of presenter"],
            ["meeting", "Meeting"],
            ["meetingDate", "Meeting Date"],
            ["publishedAsAnAbstractId", "Published as an abstract"]
        ),
        covering(
            field(
                "trialPublications",
                "Publications",
                "Papers and other publications from the trial. Add a row per publication with its type, status, details, whether the publications committee (TPC) reviewed it, and the year it was published."
            ),
            ["trialPublicationTypeId", "Publication Type"],
            ["trialPublicationTypeOther", "Other, for a publication type not listed"],
            ["trialPublicationStatusId", "Status"],
            ["details", "Details"],
            ["tPCReviewedId", "TPC Reviewed"],
            ["yearPublished", "Year Published (print)"]
        ),
        saveStop(
            "Submit saves the milestones and every row in the analysis, presentation and publication tables.",
            Permission.TrialUpdate
        )
    ]
};

const tracker: PageGuide = {
    intro: "One QA tracker: a quality-assurance activity on the trial, such as a plan review, that moves through milestones and is assigned to a person. Comments and documents record its progress.",
    stops: [
        field(
            "trackerMilestoneId",
            "Milestone",
            "The stage the activity has reached. Update it as the activity progresses; the trackers list shows it."
        ),
        field(
            "trackerTypeId",
            "Type",
            "What kind of activity the tracker follows, such as a plan review or benchmarking exercise."
        ),
        field(
            "subject",
            "Subject",
            "What or who the tracker is about, such as a site or a participant's registration number."
        ),
        field(
            "trackerStatusId",
            "Status",
            "Whether the activity is still in progress, on hold or finished."
        ),
        field(
            "assignedToId",
            "Assigned To",
            "The person responsible for the next action on the activity."
        ),
        field(
            "dateStarted",
            "Start Date",
            "When the activity started, shown in the Started column of the trackers list."
        ),
        field(
            "dateStopped",
            "Completion/Due Date",
            "When the activity is due, or the date it was completed once it has finished."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the tracker from the trackers list without deleting it."
        ),
        covering(
            field(
                "trialTrackerComments",
                "Trial Tracker Comments",
                "A running log of the activity. Add a comment for each step or conversation; the author and date are recorded with it."
            ),
            ["comments", "Comments"]
        ),
        covering(
            field(
                "sourceDocuments",
                "Source Documents",
                "Documents supporting the activity, such as review reports. Add a row per document: name it, choose its file type and upload the file."
            ),
            ["name", "Name"],
            ["fileType", "File Type"],
            ["fileInformation", "File"]
        ),
        saveStop(
            "Submit saves the tracker with its comments and documents.",
            Permission.TrialTrackerUpdate
        )
    ],
    rules: ["Milestone, Type, Subject, Status and Assigned To are required."]
};

const participantItem: PageGuide = {
    intro: "One participant item, such as a consent or eligibility record: its template, its current version and its version history. Enter Data opens the collected data.",
    stops: [
        field(
            "patientItemTemplateId",
            "Participant Item Template",
            "The template the item was created from, which sets the questions it collects. It cannot be changed once the item exists."
        ),
        ...versionStops(),
        field(
            "currentVersion.trialItemStatusId",
            "Status",
            "The current version's review state. Submit its data before marking it Complete."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the item, for example when it no longer applies to the participant."
        ),
        saveStop(
            "Submit saves the item's details. The collected data is saved on the Enter Data page.",
            Permission.PatientItemUpdate
        )
    ]
};

const trialForm: PageGuide = {
    intro: "One QA form on the trial, such as a benchmarking exercise or a case review: its template, the site or participant it concerns, who is responsible, its status and its versions. Data entry, case reports and emails are on the side menu.",
    stops: [
        field(
            "institutionId",
            "Institution",
            "The site the form concerns. It limits the participants offered below to those registered at the site."
        ),
        field(
            "formTemplateId",
            "Form Template",
            "The QA form template, chosen from those the trial uses. It sets the form's questions and whether it concerns a participant."
        ),
        field(
            "patientId",
            "Patient",
            "The participant the form concerns, for forms about a single participant such as a case review."
        ),
        field(
            "currentVersion.formStatusId",
            "Status",
            "Where the current version is in its workflow. The statuses available depend on the form's type, and Pending Review is offered only when its template requires review."
        ),
        field(
            "assignmentTypeId",
            "Assign To",
            "Whether a person or a role is responsible for completing the form. The matching field appears below."
        ),
        field(
            "assignedToId",
            "Assigned To",
            "The person responsible for completing the form, when it is assigned to a person."
        ),
        field(
            "assignedRoleId",
            "Assigned Role",
            "The role responsible for completing the form, when it is assigned to a role; anyone holding the role at the site can complete it."
        ),
        field(
            "currentVersion.version",
            "Version",
            "The name of the form's version. Create Version starts a new version, such as a resubmission, keeping earlier versions readable."
        ),
        field(
            "currentVersion.formNextAutoVersionId",
            "Form Next Auto Version",
            "Whether new versions are created by hand (Manual) or automatically a year after the version was created or completed."
        ),
        field(
            "currentVersion.dateNextVersion",
            "Date Next Version",
            "When the next version will be created automatically, for forms that create versions annually."
        ),
        field(
            "currentVersion.comments",
            "Version Comments",
            "Why this version exists or anything a reviewer should know about it, such as what changed from the last version. Earlier versions keep their own comments, which can be edited in the version history."
        ),
        saveStop(
            "Submit saves the form's details. Its answers are saved on the Data Entry page.",
            Permission.TrialFormUpdate
        )
    ],
    rules: [
        "Institution and Form Template are required, and Patient when the template is about a participant."
    ]
};

const trialFormWithMenu: PageGuide = {
    ...trialForm,
    stops: [
        ...trialForm.stops,
        menu(
            item("Details", "This page: the form's template, subject, assignment and versions."),
            item("Data Entry", "Complete or review the form's answers for its current version."),
            item(
                "Administration Case Report",
                "Each measured result of the form against its benchmark, with minor and major deviations."
            ),
            item(
                "QA Case Report",
                "The reviewers' report on the form, which can be edited and downloaded as a PDF."
            ),
            item(
                "Emails",
                "Who receives the emails sent about this form, with a test send to check them."
            )
        )
    ]
};

const treatment: PageGuide = {
    intro: "One randomisation arm of the trial, which participants are allocated to.",
    stops: [
        field(
            "openClinicaLookupValueId",
            "OpenClinica Treatment Value",
            "The matching treatment value in OpenClinica, so participants imported from OpenClinica are given the right arm."
        ),
        field(
            "name",
            "Randomisation Arm",
            "The arm's name as written in the protocol, shown when allocating participants."
        ),
        field(
            "shortName",
            "Short Name",
            "An abbreviation of the arm, used where space is short, such as in participant lists."
        ),
        field(
            "duration",
            "Duration",
            "How long treatment on the arm lasts, as described in the protocol."
        ),
        field(
            "targetAccrual",
            "Target Accrual",
            "How many participants the arm should recruit, for comparing arms in accrual reports."
        ),
        field(
            "active",
            "Active",
            "Clear to retire the arm so it is no longer offered. Participants already on it keep it."
        ),
        saveStop(
            "Submit saves the arm; it can then be chosen for participants.",
            Permission.TrialTreatmentsUpdate
        )
    ],
    rules: ["Randomisation Arm is required."]
};

const trialGroupItem: PageGuide = {
    intro: "One item collected from a group for the trial: its template, its current version and its version history. Enter Data opens the collected data.",
    stops: [
        field(
            "trialGroupItemTemplateId",
            "Trial Group Item Template",
            "The template the item was created from, which sets the questions it collects. It cannot be changed once the item exists."
        ),
        ...versionStops(),
        field(
            "currentVersion.trialItemStatusId",
            "Status",
            "The current version's review state. Submit its data before marking it Complete."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the item from the group's items, for example when it no longer applies."
        ),
        saveStop(
            "Submit saves the item's details. Enter Data opens its collected data.",
            Permission.TrialGroupUpdate
        )
    ]
};

const trialRoles: RoleOwner = {
    kind: "trial",
    owner: "this trial",
    examples: "trial chair, trial coordinator and site principal investigators",
    createPermission: Permission.TrialPersonnelRoleCreate,
    updatePermission: Permission.TrialPersonnelRoleUpdate
};

const trialNotes = notesGuides("the trial", Permission.TrialNoteCreate, Permission.TrialNoteUpdate);

const trialGuides: Record<string, PageGuide> = {
    "/dashboard": {
        intro: "Your starting point. The dashboard lists the work your roles give you access to. Each list appears only when it has something for you; select a row to open that record.",
        stops: [
            stop(
                dashboardSummaryTourTarget("Trial Institutions"),
                "Trial Institutions",
                "The sites you have roles at, by trial: their TROG number and title, the institution, its status, how many roles are held there and when it was activated."
            ),
            stop(
                dashboardSummaryTourTarget("Trials"),
                "Trials",
                "The trials you have trial-wide roles on, with their TROG number, title, status and role count."
            ),
            stop(
                dashboardSummaryTourTarget("Trial Items"),
                "Trial Items",
                "Trial items assigned to you or your roles, with their status and current version, so you can see what needs completing."
            ),
            stop(
                dashboardSummaryTourTarget("Facility Questionnaires"),
                "Facility Questionnaires",
                "Institution QA forms, such as facility questionnaires, assigned to you or your roles, with their status and version."
            ),
            stop(
                dashboardSummaryTourTarget("Audit Forms"),
                "Audit Forms",
                "Trial audit forms assigned to you or your roles, by participant, with their status and version."
            ),
            stop(
                dashboardSummaryTourTarget("Checklist Forms"),
                "Checklist Forms",
                "Trial checklist forms assigned to you or your roles, by participant, with their status and version."
            ),
            stop(
                helpTourTargets.breadcrumbs,
                "Where you are",
                "The breadcrumb trail shows how you reached a page; select any level to go back to it."
            )
        ],
        notes: [
            "Notices published for you appear at the top of the dashboard.",
            "If the dashboard says you have no roles, your account has not been given access to any trials or institutions yet. Contact TROG to have a role assigned."
        ]
    },
    "/trials": {
        intro: "Every trial you can see. Filter to find one, then select it to open its details.",
        stops: [
            filterStop(
                "Type a number, acronym or title to filter. Inactive trials are hidden unless you switch them on."
            ),
            actionsStop(
                "Create starts a new trial. Export Trial Data downloads the details and personnel of the trials shown.",
                Permission.TrialCreate
            ),
            grid("Each row is one trial; select it to open its details and side menu.", [
                column(
                    "Trial Identifiers",
                    "Every number the trial is known by: its TROG, development and proposal numbers."
                ),
                column("TROG Number", "The trial's TROG number, such as TROG 18.06."),
                column("Acronym", "The short name the trial is known by, such as PROSPECT."),
                column("Category", "How the trial is grouped for reporting."),
                column(
                    "Status",
                    "Where the trial is in its life cycle for TROG, from development to closed."
                ),
                column(
                    "OPMS",
                    "Whether the trial uses the OPMS online participant management system."
                )
            ])
        ]
    },
    "/trials/:trialId/details": trialDetailsWithMenu,
    "/trials/create": createGuide(
        trialDetails,
        "Create a new trial. Enter at least one of its numbers, its scientific title and its status; everything else can be completed later.",
        [
            "Submit creates the trial and opens it. Its side menu then lets you add institutions, personnel, items and configuration.",
            "Milestones are recorded separately, on the trial's Milestones page."
        ],
        { omit: ["active"] }
    ),
    "/trials/:trialId/milestones": trialMilestones,
    "/trials/:trialId/trackers": {
        intro: "The trial's QA trackers: quality-assurance activities, such as plan reviews, that move through milestones until they are finished.",
        stops: [
            filterStop(
                "Type a subject, milestone, type or status to filter. Hidden trackers appear when you switch on inactive records."
            ),
            actionsStop(
                "Add starts a new tracker for a QA activity.",
                Permission.TrialTrackerCreate
            ),
            grid("Each row is one tracker; select it to update it or add comments.", [
                column("Milestone", "The stage the activity has reached."),
                column("Subject", "What or who the activity is about, such as a site."),
                column("Started", "When the activity started."),
                column("Type", "What kind of activity it is, such as a plan review."),
                column("Entered By", "The person who created the tracker."),
                column("Status", "Whether the activity is in progress, on hold or finished."),
                column("Completed/Due", "When the activity is due, or when it was completed.")
            ])
        ]
    },
    "/trials/:trialId/trackers/:trialTrackerId": tracker,
    "/trials/:trialId/trackers/create": createGuide(
        tracker,
        "Start a QA tracker. Choose its type and first milestone, what it is about, and who is responsible.",
        ["Once saved, add comments and documents and move it through its milestones."],
        { omit: ["active", "dateStopped"] }
    ),
    "/trials/:trialId/qa-program-checklist": {
        intro: "The trial's QA program checklist: every task in its quality-assurance program, grouped by category, with its completion state.",
        stops: [
            filterStop("Type part of a task or category name to filter the checklist."),
            actionsStop(
                "Manage opens the checklist configuration, where categories and tasks are set up.",
                Permission.TrialQaProgramChecklistUpdate
            )
        ],
        notes: [
            "Mark each task done, not done or not applicable, and add notes and attachments as evidence. Every change of state is kept in the task's history.",
            "Pin the checklist to keep it beside any trial page while you work."
        ]
    },
    "/trials/:trialId/notes": trialNotes.list,
    "/trials/:trialId/notes/:noteId": trialNotes.item,
    "/trials/:trialId/notes/create": trialNotes.create,
    "/trials/:trialId/participants": {
        intro: "Every participant registered on the trial, across all sites.",
        stops: [
            filterStop(
                "Type a registration number, initials, treatment or institution to filter. Withdrawn participants are hidden unless you switch on inactive records."
            ),
            actionsStop(
                "Add registers a participant. Import creates participants that exist in OpenClinica but not yet here; existing participants are not changed.",
                Permission.TrialPatientCreate
            ),
            grid(
                "Each row is one participant; select it to open them. A lock on the icon means the participant is linked to OpenClinica.",
                participantColumns(true)
            )
        ]
    },
    "/trials/:trialId/participants/:patientId/details": participantGuide(
        Permission.TrialPatientUpdate,
        true
    ),
    "/trials/:trialId/participants/create": createGuide(
        participantGuide(Permission.TrialPatientCreate, false),
        "Register a participant on the trial. Choose the site first, then enter their initials and registration number.",
        [
            "Once saved, the participant's items, emails and administration case report are available from the side menu."
        ],
        { omit: ["active"] }
    ),
    "/trials/:trialId/participants/:patientId/administrationcasereport": {
        intro: "The participant's administration case report: every measured result from the QA forms completed for them, with its classification against the benchmark.",
        stops: [
            grid("Each row is one measured result from one of the participant's QA forms.", [
                column("Form", "The QA form the result comes from."),
                column("Category", "The part of the form the result belongs to."),
                column("Field", "What was measured, such as a dose or a margin."),
                column("Result", "The value submitted on the form."),
                column(
                    "Classification",
                    "How the result compares with the benchmark, such as within tolerance or a deviation."
                ),
                column("Benchmark", "The value or range the result is compared with."),
                column("MIN", "Marked when the result is a minor deviation from the benchmark."),
                column("MAJ", "Marked when the result is a major deviation from the benchmark.")
            ])
        ]
    },
    "/trials/:trialId/participants/:patientId/emails": emailRecipientsGuide(
        "participant",
        "Who receives the emails sent about this participant. The participant follows the site's recipients until they are set here.",
        Permission.TrialPatientEmailRecipientsUpdate
    ),
    "/trials/:trialId/participants/:patientId/items": {
        intro: "The trial's participant items, grouped by item category: the data and documents collected for participants, created from the trial's participant item templates.",
        stops: [
            filterStop(
                "Type a participant, item or institution to filter. Inactive items are hidden unless you switch on inactive records."
            ),
            actionsStop(
                "Bulk Update enters the same item for many participants on one page.",
                Permission.PatientItemUpdate
            ),
            grid(
                "Each row is one participant's item. Select it to open the item, or use its menu to open one of its versions.",
                [
                    column("Participant", "The participant's registration number."),
                    column("Initials", "The participant's initials."),
                    column("Institution", "The site the participant is registered at."),
                    column("Name", "The item, from its participant item template."),
                    column(
                        "Status",
                        "Whether the current version awaits entry, review or is Complete."
                    ),
                    column(
                        "Version",
                        "The item's current version, with earlier versions in its menu."
                    )
                ]
            )
        ]
    },
    "/trials/:trialId/participants/:patientId/items/:patientItemId": participantItem,
    "/trials/:trialId/participants/:patientId/items/create": createGuide(
        participantItem,
        "Add an item for this participant from one of the trial's participant item templates, with its first version.",
        ["Once saved, enter its data from Enter Data."],
        { omit: ["active"] }
    ),
    "/trials/:trialId/participants/:patientId/items/:patientItemId/entry": entryGuide(
        "this participant item",
        Permission.PatientItemUpdate
    ),
    "/trials/:trialId/personnel": roleListGuide(trialRoles),
    "/trials/:trialId/personnel/create": roleCreateGuide(trialRoles),
    "/trials/:trialId/personnel/:personnelRoleId": roleEditGuide(trialRoles),
    "/trials/:trialId/groups": {
        intro: "The cooperative groups taking part in the trial. Items are collected from each group, from the trial's group item templates.",
        stops: [
            filterStop("Type part of a group's name to filter the list."),
            actionsStop(
                "Add links another cooperative group to the trial so items can be collected from it.",
                Permission.TrialGroupCreate
            ),
            grid("Each row is one group; select it to open its participation and items.", [
                column("Name", "The group taking part in the trial.")
            ])
        ]
    },
    "/trials/:trialId/groups/:trialGroupId/details": {
        intro: "One group's participation in the trial. The items collected from the group are on the side menu.",
        stops: [
            field(
                "groupId",
                "Group",
                "The cooperative group taking part, chosen from the groups maintained under Groups."
            ),
            field(
                "active",
                "Active",
                "Clear to end the group's participation. Its items are kept."
            ),
            saveStop(
                "Submit saves the group's participation; its items stay available from the side menu.",
                Permission.TrialGroupUpdate
            )
        ]
    },
    "/trials/:trialId/groups/create/details": {
        intro: "Link a group to the trial so items can be collected from it.",
        stops: [
            field(
                "groupId",
                "Group",
                "The cooperative group taking part, chosen from the groups maintained under Groups."
            ),
            saveStop(
                "Submit links the group; its items are created from the trial's group item templates.",
                Permission.TrialGroupCreate
            )
        ],
        notShown: ["active"]
    },
    "/trials/:trialId/groups/:trialGroupId/items": {
        intro: "The items collected from this group for the trial, created from the trial's group item templates.",
        stops: [
            grid("Each row is one item; select it to open its versions and data.", [
                column("Group", "The group the item is collected from."),
                column("Name", "The item, from its group item template."),
                column("Description", "What the item collects, from its template."),
                column(
                    "Status",
                    "Whether the current version awaits entry, review or is Complete."
                ),
                column("Versions", "How many versions the item has.")
            ])
        ]
    },
    "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/details": trialGroupItem,
    "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/entry": entryGuide(
        "this group item",
        Permission.TrialGroupUpdate
    ),
    "/trials/:trialId/treatments": {
        intro: "The trial's treatments (randomisation arms). Participants are allocated to one of them.",
        stops: [
            filterStop("Type part of an arm's name or short name to filter the list."),
            actionsStop(
                "Sync from OpenClinica creates, updates and reactivates arms to match the trial's OpenClinica treatment values, and reports what changed. Add creates an arm by hand.",
                Permission.TrialTreatmentsCreate
            ),
            grid("Each row is one arm; select it to change it.", [
                column("Randomisation Arm", "The arm's name, as written in the protocol."),
                column("Short Name", "The abbreviation used where space is short."),
                column("Duration", "How long treatment on the arm lasts."),
                column("Target Accrual", "How many participants the arm should recruit.")
            ])
        ]
    },
    "/trials/:trialId/treatments/:trialTreatmentId": treatment,
    "/trials/:trialId/treatments/create": createGuide(
        treatment,
        "Add a randomisation arm to the trial. Match it to its OpenClinica treatment value when the trial uses OpenClinica.",
        ["Participants can be allocated to the arm once it is saved."],
        { omit: ["active"] }
    ),
    "/trials/:trialId/forms": {
        intro: "The trial's QA forms, such as benchmarking exercises and case reviews, for its sites and participants.",
        stops: [
            filterStop(
                "Type a participant, institution, assignee, status or template to filter the list."
            ),
            actionsStop(
                "Add creates a QA form for a site or participant.",
                Permission.TrialFormCreate
            ),
            grid("Each row is one form; select it to open its details and case reports.", [
                column("Patient", "The participant the form concerns, for participant forms."),
                column("Institution", "The site the form concerns."),
                column("Assigned To", "The person or role responsible for completing the form."),
                column("Status", "Where the current version is in its workflow."),
                column("Versions", "How many versions the form has, such as resubmissions."),
                column("Form Template", "The QA form template the form was built from.")
            ])
        ]
    },
    "/trials/:trialId/forms/:trialFormId": trialFormWithMenu,
    "/trials/:trialId/forms/create": createGuide(
        trialForm,
        "Create a QA form. Choose the site and template, and the participant when the form is about one, then assign it.",
        [
            "Once saved, the assignee completes it from Data Entry, and emails are sent according to the trial's email settings."
        ],
        {}
    ),
    "/trials/:trialId/forms/:trialFormId/report": {
        intro: "The QA case report for this form: the review of what was submitted, its results against benchmarks, and the reviewers' conclusions. The report can be downloaded as a PDF.",
        stops: [
            stop(
                helpTourTargets.pageActions,
                "Report actions",
                "Edit changes the report's reviewers, date and sections; the PDF action downloads the report as it will be sent.",
                Permission.TrialFormUpdate
            )
        ]
    },
    "/trials/:trialId/forms/:trialFormId/report/edit": {
        intro: "Edit the QA case report: its reviewers, review date and sections. Each section can be previewed as it will appear in the PDF.",
        stops: [
            field(
                "reviewers",
                "Reviewers",
                "The people who reviewed the form; they are named on the report."
            ),
            field(
                "reviewDate",
                "Review Date",
                "When the review was completed, as printed on the report."
            ),
            saveStop(
                "Submit saves the report; download the PDF from the report page afterwards.",
                Permission.TrialFormUpdate
            )
        ],
        rules: ["Report identity, table and graph sections may only appear once."]
    },
    "/trials/:trialId/forms/:trialFormId/administrationcasereport": {
        intro: "The administration case report: each measured result of the form with its classification against the benchmark.",
        stops: [
            grid("Each row is one measured result from the form.", [
                column("Category", "The part of the form the result belongs to."),
                column("Field", "What was measured, such as a dose or a margin."),
                column("Result", "The value submitted on the form."),
                column(
                    "Classification",
                    "How the result compares with the benchmark, such as within tolerance or a deviation."
                ),
                column("Benchmark", "The value or range the result is compared with."),
                column("MIN", "Marked when the result is a minor deviation from the benchmark."),
                column("MAJ", "Marked when the result is a major deviation from the benchmark.")
            ])
        ]
    },
    "/trials/:trialId/forms/:trialFormId/emails": {
        ...emailRecipientsGuide(
            "caseReport",
            "Who receives the emails sent about this form. The form follows the participant's or site's recipients until they are set here.",
            Permission.TrialFormEmailRecipientsUpdate
        ),
        notes: [
            "Send a test to me, under an email's settings, sends that email for this form to you so you can check its content and recipients. Save your changes first; a test can take a minute to arrive.",
            "Levels run from global to trial, site, participant and case report. Each level follows the one above it until it sets its own recipients, and a more specific level wins."
        ]
    },
    "/trials/:trialId/forms/:trialFormId/entry": entryGuide(
        "this QA form",
        Permission.TrialFormDataEntry,
        "Details"
    )
};

export default trialGuides;
